import React, { useEffect, useState, useCallback } from 'react';
import { Box, Typography, CircularProgress } from '@mui/material';

interface AudioAnalyzerProps {
  recordedAudio: Blob;
  targetAudio: string;
  onAnalysisComplete: (score: number, feedback: string) => void;
}

const AudioAnalyzer: React.FC<AudioAnalyzerProps> = ({
  recordedAudio,
  targetAudio,
  onAnalysisComplete
}) => {
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [score, setScore] = useState<number | null>(null);
  const [feedback, setFeedback] = useState<string>('');
  const [lastAnalyzedBlob, setLastAnalyzedBlob] = useState<Blob | null>(null);

  const generateFeedback = useCallback((score: number): string => {
    if (score >= 90) return '发音非常准确！';
    if (score >= 80) return '发音很好！';
    if (score >= 70) return '发音基本准确';
    if (score >= 60) return '需要改进';
    return '建议多练习';
  }, []);

  // 分析音频的频率特征
  const analyzeFrequencyFeatures = async (audioBuffer: AudioBuffer, audioContext: AudioContext): Promise<Float32Array> => {
    const analyser = audioContext.createAnalyser();
    analyser.fftSize = 2048;
    analyser.smoothingTimeConstant = 0.8;
    analyser.minDecibels = -90;
    analyser.maxDecibels = -10;
    
    const source = audioContext.createBufferSource();
    source.buffer = audioBuffer;
    source.connect(analyser);
    
    // 创建一个新的 AudioBuffer 来存储处理后的数据
    const processedBuffer = audioContext.createBuffer(
      audioBuffer.numberOfChannels,
      audioBuffer.length,
      audioBuffer.sampleRate
    );
    
    // 复制并处理音频数据，应用预加重滤波器增强高频
    for (let channel = 0; channel < audioBuffer.numberOfChannels; channel++) {
      const inputData = audioBuffer.getChannelData(channel);
      const outputData = processedBuffer.getChannelData(channel);
      
      // 应用预加重滤波器，alpha = 0.97
      outputData[0] = inputData[0];
      for (let i = 1; i < inputData.length; i++) {
        outputData[i] = inputData[i] - 0.97 * inputData[i - 1];
      }
    }
    
    // 使用处理后的数据创建新的源
    const processedSource = audioContext.createBufferSource();
    processedSource.buffer = processedBuffer;
    processedSource.connect(analyser);
    
    const frequencyData = new Float32Array(analyser.frequencyBinCount);
    const timeData = new Float32Array(analyser.frequencyBinCount);
    
    return new Promise((resolve) => {
      // 收集多个时间点的频率数据
      const samples: Float32Array[] = [];
      let sampleCount = 0;
      const maxSamples = 10;
      
      const collectData = () => {
        analyser.getFloatFrequencyData(frequencyData);
        analyser.getFloatTimeDomainData(timeData);
        
        // 只关注有声音的部分
        let hasSound = false;
        let rms = 0;
        for (let i = 0; i < timeData.length; i++) {
          rms += timeData[i] * timeData[i];
        }
        rms = Math.sqrt(rms / timeData.length);
        hasSound = rms > 0.01;
        
        if (hasSound) {
          // 复制当前频率数据
          const currentSample = new Float32Array(frequencyData);
          samples.push(currentSample);
          sampleCount++;
        }
        
        if (sampleCount < maxSamples) {
          requestAnimationFrame(collectData);
        } else {
          // 计算平均频率特征
          const averageFeatures = new Float32Array(frequencyData.length);
          for (let i = 0; i < frequencyData.length; i++) {
            let sum = 0;
            for (const sample of samples) {
              sum += sample[i];
            }
            averageFeatures[i] = sum / samples.length;
          }
          resolve(averageFeatures);
        }
      };
      
      processedSource.start(0);
      collectData();
    });
  };

  // 计算音频波形的 RMS 能量
  const calculateRMSEnergy = (audioBuffer: AudioBuffer): number => {
    const samples = audioBuffer.getChannelData(0);
    let sum = 0;
    let count = 0;
    
    // 每 100 个样本取一个，减少计算量
    for (let i = 0; i < samples.length; i += 100) {
      sum += samples[i] * samples[i];
      count++;
    }
    
    const rms = Math.sqrt(sum / count);
    console.log('RMS Energy:', rms);
    return rms;
  };

  // 计算两个频率特征的相似度
  const calculateFrequencySimilarity = (freq1: Float32Array, freq2: Float32Array, sampleRate: number): number => {
    try {
      const len = Math.min(freq1.length, freq2.length);
      if (len === 0) {
        console.error('Empty frequency data');
        return 0;
      }

      // 定义元音的共振峰频率范围（以Hz为单位）
      const formantRanges = [
        { min: 200, max: 800 },   // F1: 第一共振峰
        { min: 800, max: 2500 },  // F2: 第二共振峰
        { min: 2500, max: 3500 }  // F3: 第三共振峰
      ];

      let totalSimilarity = 0;
      let totalWeight = 0;

      // 分别计算每个共振峰范围的相似度
      formantRanges.forEach((range, index) => {
        const startBin = Math.floor(range.min * len / sampleRate);
        const endBin = Math.ceil(range.max * len / sampleRate);
        
        let rangeSimilarity = 0;
        let rangeWeight = 0;
        let peakFreq1 = -Infinity;
        let peakFreq2 = -Infinity;
        let peakBin1 = startBin;
        let peakBin2 = startBin;

        // 在每个范围内找到峰值
        for (let i = startBin; i < endBin; i++) {
          if (freq1[i] > peakFreq1) {
            peakFreq1 = freq1[i];
            peakBin1 = i;
          }
          if (freq2[i] > peakFreq2) {
            peakFreq2 = freq2[i];
            peakBin2 = i;
          }
        }

        // 计算峰值位置的差异
        const binDiff = Math.abs(peakBin1 - peakBin2);
        const maxBinDiff = endBin - startBin;
        const positionSimilarity = 100 * (1 - binDiff / maxBinDiff);

        // 计算峰值强度的差异
        const intensityDiff = Math.abs(peakFreq1 - peakFreq2);
        const intensitySimilarity = 100 * (1 - Math.min(intensityDiff / 100, 1));

        // 综合位置和强度的相似度
        rangeSimilarity = (positionSimilarity * 0.7 + intensitySimilarity * 0.3);
        
        // 不同共振峰的权重
        const formantWeights = [0.4, 0.4, 0.2]; // F1和F2最重要，F3次要
        rangeWeight = formantWeights[index];

        totalSimilarity += rangeSimilarity * rangeWeight;
        totalWeight += rangeWeight;

        console.log(`Formant ${index + 1} Analysis:`, {
          range: `${range.min}-${range.max}Hz`,
          peakBin1,
          peakBin2,
          peakFreq1,
          peakFreq2,
          positionSimilarity,
          intensitySimilarity,
          rangeSimilarity
        });
      });

      const finalSimilarity = totalSimilarity / totalWeight;
      
      console.log('Formant Analysis Summary:', {
        finalSimilarity,
        sampleRate,
        binCount: len
      });
      
      return Math.max(0, Math.min(100, finalSimilarity));
    } catch (error) {
      console.error('Error calculating frequency similarity:', error);
      return 0;
    }
  };

  // 分析音频持续时间
  const analyzeDuration = useCallback((targetDuration: number, recordedDuration: number): number => {
    try {
      const durationDiff = Math.abs(targetDuration - recordedDuration);
      const maxAllowedDiff = 1.0; // 最大允许1秒的差异
      const score = Math.max(0, 100 - (durationDiff / maxAllowedDiff * 100));
      
      console.log('Duration Analysis:', {
        targetDuration,
        recordedDuration,
        durationDiff,
        score
      });
      
      return score;
    } catch (error) {
      console.error('Error analyzing duration:', error);
      return 0;
    }
  }, []);

  // 分析音量差异
  const analyzeVolume = (targetEnergy: number, recordedEnergy: number): number => {
    try {
      if (isNaN(targetEnergy) || isNaN(recordedEnergy)) {
        console.error('Invalid energy values:', { targetEnergy, recordedEnergy });
        return 0;
      }
      
      const volumeDiff = Math.abs(targetEnergy - recordedEnergy);
      const maxAllowedDiff = 0.5;
      const score = Math.max(0, 100 - (volumeDiff / maxAllowedDiff * 100));
      
      console.log('Volume Analysis:', {
        targetEnergy,
        recordedEnergy,
        volumeDiff,
        score
      });
      
      return score;
    } catch (error) {
      console.error('Error analyzing volume:', error);
      return 0;
    }
  };

  // 计算 Mel 频率
  const freqToMel = (freq: number): number => {
    return 2595 * Math.log10(1 + freq / 700);
  };

  // 计算 Mel 到频率的转换
  const melToFreq = (mel: number): number => {
    return 700 * (Math.pow(10, mel / 2595) - 1);
  };

  // 创建 Mel 滤波器组
  const createMelFilterbank = (fftSize: number, sampleRate: number, numFilters: number): Float32Array[] => {
    const filters: Float32Array[] = [];
    const melMin = freqToMel(0);
    const melMax = freqToMel(sampleRate / 2);
    const melStep = (melMax - melMin) / (numFilters + 1);

    // 创建 Mel 频率点
    const melPoints = new Array(numFilters + 2);
    for (let i = 0; i < melPoints.length; i++) {
      melPoints[i] = melToFreq(melMin + i * melStep);
    }

    // 创建滤波器
    const fftFreqs = new Array(fftSize / 2 + 1);
    for (let i = 0; i < fftFreqs.length; i++) {
      fftFreqs[i] = i * sampleRate / fftSize;
    }

    for (let i = 0; i < numFilters; i++) {
      const filter = new Float32Array(fftSize / 2 + 1).fill(0);
      const f_m_minus = melPoints[i];
      const f_m = melPoints[i + 1];
      const f_m_plus = melPoints[i + 2];

      for (let j = 0; j < fftFreqs.length; j++) {
        const freq = fftFreqs[j];
        if (freq >= f_m_minus && freq <= f_m) {
          filter[j] = (freq - f_m_minus) / (f_m - f_m_minus);
        } else if (freq >= f_m && freq <= f_m_plus) {
          filter[j] = (f_m_plus - freq) / (f_m_plus - f_m);
        }
      }
      filters.push(filter);
    }

    return filters;
  };

  // 计算 MFCC 特征
  const computeMFCC = (spectrum: Float32Array, melFilters: Float32Array[], numCoefficients: number): Float32Array => {
    const melSpectrum = new Float32Array(melFilters.length);
    
    // 应用 Mel 滤波器
    for (let i = 0; i < melFilters.length; i++) {
      let sum = 0;
      for (let j = 0; j < spectrum.length; j++) {
        sum += Math.abs(spectrum[j]) * melFilters[i][j];
      }
      melSpectrum[i] = Math.log(sum + 1e-6);
    }

    // 应用 DCT
    const mfcc = new Float32Array(numCoefficients);
    for (let i = 0; i < numCoefficients; i++) {
      let sum = 0;
      for (let j = 0; j < melFilters.length; j++) {
        sum += melSpectrum[j] * Math.cos((i * Math.PI / melFilters.length) * (j + 0.5));
      }
      mfcc[i] = sum;
    }

    return mfcc;
  };

  // 分析音频的 MFCC 特征
  const analyzeMFCC = async (audioBuffer: AudioBuffer, audioContext: AudioContext): Promise<Float32Array[]> => {
    const analyser = audioContext.createAnalyser();
    analyser.fftSize = 2048;
    
    const source = audioContext.createBufferSource();
    source.buffer = audioBuffer;
    source.connect(analyser);
    // 移除到输出设备的连接，只连接到分析器
    // analyser.connect(audioContext.destination);
    
    const numFilters = 26;
    const numCoefficients = 13;
    const melFilters = createMelFilterbank(analyser.fftSize, audioContext.sampleRate, numFilters);
    
    const frequencyData = new Float32Array(analyser.frequencyBinCount);
    const mfccFrames: Float32Array[] = [];
    
    return new Promise((resolve) => {
      let frameCount = 0;
      const maxFrames = 20;  // 增加采样帧数以获得更多数据点
      
      const collectData = () => {
        analyser.getFloatFrequencyData(frequencyData);
        
        // 检查是否有有效的音频数据
        const hasValidData = frequencyData.some(value => value > -100);  // 典型的背景噪声阈值
        
        if (hasValidData) {
          const mfcc = computeMFCC(frequencyData, melFilters, numCoefficients);
          mfccFrames.push(mfcc);
          frameCount++;
        }
        
        if (frameCount < maxFrames) {
          requestAnimationFrame(collectData);
        } else {
          source.stop();  // 停止音频源
          resolve(mfccFrames);
        }
      };
      
      // 添加延迟以确保音频开始播放后再采集数据
      setTimeout(() => {
        source.start(0);
        collectData();
      }, 100);
    });
  };

  // 计算 DTW 距离时添加归一化处理
  const computeDTW = (seq1: Float32Array[], seq2: Float32Array[]): number => {
    // 对 MFCC 系数进行归一化处理
    const normalizeSequence = (seq: Float32Array[]): Float32Array[] => {
      const normalized = seq.map(frame => {
        const mean = frame.reduce((a, b) => a + b, 0) / frame.length;
        const std = Math.sqrt(frame.reduce((a, b) => a + (b - mean) ** 2, 0) / frame.length);
        return frame.map(value => (value - mean) / (std + 1e-6));
      });
      return normalized;
    };

    const normalizedSeq1 = normalizeSequence(seq1);
    const normalizedSeq2 = normalizeSequence(seq2);

    const n = normalizedSeq1.length;
    const m = normalizedSeq2.length;
    const dtw = Array(n + 1).fill(null).map(() => Array(m + 1).fill(Infinity));
    dtw[0][0] = 0;

    // 计算欧氏距离
    const distance = (a: Float32Array, b: Float32Array): number => {
      let sum = 0;
      for (let i = 0; i < a.length; i++) {
        sum += Math.pow(a[i] - b[i], 2);
      }
      return Math.sqrt(sum);
    };

    // 填充 DTW 矩阵
    for (let i = 1; i <= n; i++) {
      for (let j = 1; j <= m; j++) {
        const cost = distance(normalizedSeq1[i-1], normalizedSeq2[j-1]);
        dtw[i][j] = cost + Math.min(
          dtw[i-1][j],    // 插入
          dtw[i][j-1],    // 删除
          dtw[i-1][j-1]   // 匹配
        );
      }
    }

    // 返回归一化的 DTW 距离
    return dtw[n][m] / (n + m);
  };

  useEffect(() => {
    const isNewRecording = recordedAudio !== lastAnalyzedBlob;
    
    const analyzeAudio = async () => {
      if (!isNewRecording) return;
      
      setIsAnalyzing(true);
      setLastAnalyzedBlob(recordedAudio);
      
      try {
        // 使用普通的 AudioContext，但不连接到输出设备
        const audioContext = new AudioContext({ sampleRate: 44100 });
        
        // 获取目标音频
        const targetResponse = await fetch(targetAudio);
        const targetArrayBuffer = await targetResponse.arrayBuffer();
        const targetAudioBuffer = await audioContext.decodeAudioData(targetArrayBuffer.slice(0));
        
        // 获取录音音频
        const recordedArrayBuffer = await recordedAudio.arrayBuffer();
        const recordedAudioBuffer = await audioContext.decodeAudioData(recordedArrayBuffer.slice(0));

        console.log('Starting audio analysis...', {
          targetDuration: targetAudioBuffer.duration,
          recordedDuration: recordedAudioBuffer.duration,
          targetSampleRate: targetAudioBuffer.sampleRate,
          recordedSampleRate: recordedAudioBuffer.sampleRate
        });

        // 计算 MFCC 特征
        const targetMFCC = await analyzeMFCC(targetAudioBuffer, audioContext);
        console.log('Target MFCC frames:', targetMFCC.length);
        
        const recordedMFCC = await analyzeMFCC(recordedAudioBuffer, audioContext);
        console.log('Recorded MFCC frames:', recordedMFCC.length);

        // 使用 DTW 计算相似度
        const dtwDistance = computeDTW(targetMFCC, recordedMFCC);
        console.log('DTW distance:', dtwDistance);
        
        // 将 DTW 距离转换为分数（距离越小，分数越高）
        const maxDistance = 5; // 调整为更合理的阈值
        const frequencyScore = Math.max(0, 100 * (1 - dtwDistance / maxDistance));

        // 分析持续时间
        const durationScore = analyzeDuration(targetAudioBuffer.duration, recordedAudioBuffer.duration);

        // 计算最终得分
        const finalScore = Math.round(frequencyScore * 0.8 + durationScore * 0.2);
        
        // 生成反馈
        const feedback = generateFeedback(finalScore);
        
        setScore(finalScore);
        setFeedback(feedback);
        onAnalysisComplete(finalScore, feedback);

        // 关闭音频上下文
        await audioContext.close();
      } catch (error) {
        console.error('Error analyzing audio:', error);
        setScore(0);
        setFeedback('分析出错，请重试');
        onAnalysisComplete(0, '分析出错，请重试');
      } finally {
        setIsAnalyzing(false);
      }
    };

    analyzeAudio();
  }, [recordedAudio, targetAudio, lastAnalyzedBlob, onAnalysisComplete, generateFeedback, analyzeDuration]);

  return (
    <Box>
      {isAnalyzing ? (
        <Box display="flex" alignItems="center" gap={1}>
          <CircularProgress size={16} />
          <Typography variant="caption">分析中...</Typography>
        </Box>
      ) : (
        score !== null && (
          <Box display="flex" alignItems="center" gap={1}>
            <Typography variant="body2">
              评分: {score}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              ({feedback})
            </Typography>
          </Box>
        )
      )}
    </Box>
  );
};

export default AudioAnalyzer; 